import { useCallback, useMemo, useState } from 'react';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import { Box } from '@mui/material';
import { Account } from '../../../../../../../shared/types/modules/account';
import { Accordion } from '../../../../../../components/general/accordion';
import { Table } from '../../../../../../components/general/table';
import { PQR_DETAIL } from '../../../../../../../routes/paths';
import { GridColDef } from '@mui/x-data-grid';
import { PqrFilters } from '../filters/PqrFilters';
import { useInteractionsData } from '../hooks/useInteractionsData';
import DateTimeFormat from '../../../../../../components/dateTimeFormat';
import { generatePqrColumns } from '../shared/columns';

type Props = {
    account?: Account;
    defaultExpanded?: boolean;
};

export const InteractionHistoryPQR = ({
    account,
    defaultExpanded = false,
}: Props) => {
    const [open, setOpen] = useState<boolean>(defaultExpanded);

    const pqrColumnsFactory = useMemo<GridColDef[]>(
        () => [
            {
                field: 'createdAt',
                headerName: 'Fecha de creación',
                flex: 1,
                minWidth: 160,
                renderCell: (p) => (
                    <>
                        <DateTimeFormat dateAt={p.row.createdAt} />
                    </>
                ),
            },
            { field: 'caseId', headerName: 'ID PQR', flex: 1, minWidth: 140 },
            {
                field: 'typification',
                headerName: 'Tipificación',
                flex: 1.2,
                minWidth: 200,
            },
            {
                field: 'observation',
                headerName: 'Observación',
                flex: 1.4,
                minWidth: 220,
            },
            {
                field: 'caseStatus',
                headerName: 'Estado caso',
                flex: 1,
                minWidth: 120,
            },
            { field: 'plate', headerName: 'Placa', flex: 1, minWidth: 120 },
            { field: 'policy', headerName: 'Póliza', flex: 1, minWidth: 140 },
            { field: 'callId', headerName: 'Call ID', flex: 1, minWidth: 140 },
            {
                field: 'userName',
                headerName: 'Usuario',
                flex: 1,
                minWidth: 140,
            },
        ],
        []
    );

    const columns = useMemo(generatePqrColumns, []);

    const {
        rows,
        rowCount,
        loading,
        paginationModel,
        setPaginationModel,
        onSubmitFilters,
        onResetFilters,
        search,
        enable,
    } = useInteractionsData({
        endpoint: 'pqr',
        initialFilters: { identification: account?.document ?? '' },
        pageSize: 20,
        columnsFactory: () => pqrColumnsFactory,
        autoload: false,
    });

    const handleAccordionChange = useCallback(
        (_event: React.SyntheticEvent, isOpen: boolean) => {
            setOpen(isOpen);
            if (isOpen) enable();
        },
        [enable]
    );

    return (
        <Accordion
            title="HISTÓRICO PQR"
            icon={<EmojiPeopleIcon sx={{ color: 'primary.main' }} />}
            mt={0}
            expanded={open}
            defaultExpanded={false}
            onChange={handleAccordionChange}
        >
            <PqrFilters
                initialValues={{ identification: account?.document ?? '' }}
                identificationDisabled={!!account?.document}
                submitting={loading}
                onSubmit={onSubmitFilters}
                onSearch={search}
                onReset={() => {
                    onResetFilters();
                }}
            />

            <Box>
                <Table
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onRowClick={(item) => {
                        const caseId = item?.row?.caseId;
                        if (caseId)
                            window.open(`${PQR_DETAIL}/${caseId}`, '_blank');
                    }}
                />
            </Box>
        </Accordion>
    );
};

export default InteractionHistoryPQR;
